import { jsonAssignInlineVars } from '@vuddy/utils';
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Element } from '../../element/Element';
import { overlay } from '../overlay.css';
import * as styles from './bottomsheet.css';
import { useBottomSheetSwipeActions } from './useBottomSheetSwipeActions';

interface BottomSheetProps {
  children: ReactNode;
  onClose: (isConfirm: boolean) => void;
  containerStyle?: CSSProperties;
  overlayStyle?: CSSProperties;
  shouldCloseOnOverlayClick?: boolean;
  duration?: number;
  maxWidth?: string;
  title?: string;
}

export function BottomSheet({
  children,
  onClose,
  containerStyle,
  overlayStyle,
  shouldCloseOnOverlayClick = true,
  duration = 200,
  maxWidth = '680px',
  title,
}: BottomSheetProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isOpened, setIsOpened] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, duration);
  };

  useBottomSheetSwipeActions({
    duration,
    overlayRef,
    handleRef,
    contentRef,
    onSwipeClose: handleClose,
  });

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (shouldCloseOnOverlayClick && e.target === overlayRef.current) {
      handleClose();
    }
  };

  useEffect(() => {
    let animationFrameId = 0;
    let animationStartFrameId = 0;

    animationFrameId = window.requestAnimationFrame(() => {
      setIsVisible(true);

      animationStartFrameId = window.requestAnimationFrame(() => {
        setIsOpened(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.cancelAnimationFrame(animationStartFrameId);
    };
  }, []);

  return (
    <div
      ref={overlayRef}
      className={overlay}
      style={{
        transition: `opacity ${duration}ms ease-out`,
        opacity: isClosing ? 0 : 1,
        ...overlayStyle,
        // Prevent a temporary overlay scrollbar while the sheet animates in from below the viewport.
        overflow: 'hidden',
      }}
      onClick={handleOverlayClick}>
      <div
        ref={contentRef}
        className={styles.bottomSheet}
        style={{
          ...jsonAssignInlineVars({
            [styles.maxWidth]: maxWidth,
          }),
          position: 'absolute',
          backgroundColor: 'white',
          bottom: 0,
          left: 0,
          right: 0,
          width: '100%',
          borderRadius: '24px 24px 0 0',
          minHeight: '130px',
          ...containerStyle,
          visibility: isVisible ? 'visible' : 'hidden',
          transform:
            isClosing || !isOpened ? 'translateY(100%)' : 'translateY(0)',
          transition: `transform ${duration}ms ease-out`,
          willChange: 'transform',
        }}>
        <div ref={handleRef} className={styles.handle} />
        {title && (
          <div className={styles.title}>
            <Element typography="bodySmallAccent" color="primary">
              {title}
            </Element>
          </div>
        )}
        <div className={styles.contents}>{children}</div>
      </div>
    </div>
  );
}
